// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import loadPurchasedProducts from "@insite/client-framework/Store/Components/PurchasedProducts/Handlers/LoadPurchasedProducts";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LinesHeader, { LinesHeaderStyles } from "@vanmeter/App/Components/Cart/LinesHeader";
import XngCarousel, { CarouselStyle } from "@vanmeter/App/Components/XngCarousel";
import useSeoSchema from "@vanmeter/App/Hooks/useRegisterSeoSchema";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@vanmeter/Overrides/Components/ProductCarouselProduct";
import PurchasedProductCard from "@vanmeter/Overrides/Components/PurchasedProductCard";
import { ContentWrapper } from "@vanmeter/Overrides/Widgets/Common/ProductCarousel";
import { CarouselCardType } from "@vanmeter/XDL/types";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    titlePosition = "titlePosition",
    purchaseType = "purchaseType",
    showBrand = "showBrand",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    // XNG-Changes
    view = "view",
    showImage = "showImage",
    showTitle = "showTitle",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    showBrandName = "showBrandName",
    enableSeo = "enableSeo",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titlePosition]: string;
        [fields.purchaseType]: string;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        // XNG-Changes
        [fields.view]: CarouselCardType;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.showBrandName]: boolean;
        [fields.enableSeo]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        canViewPurchasedProducts: isAuthenticated,
        // XNG-Changes
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    loadPurchasedProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchasedProductsStyles {
    titleWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    headerContainer?: GridContainerProps;
    itemHeaderGridItem?: GridItemProps;
    itemHeaderText?: TypographyPresentationProps;
    priceHeaderGridItem?: GridItemProps;
    priceHeaderText?: TypographyPresentationProps;
    addToCartHeaderGridItem?: GridItemProps;
    addToCartHeaderText?: TypographyPresentationProps;
    // XNG-Change
    carouselProductMiniCardStyles?: ProductCarouselProductStyles;
    xngCarouselStyles?: CarouselStyle;
    carouselProductCondensedStyles?: ProductCarouselProductStyles;
    carouselProductStyles?: ProductCarouselProductStyles;
    linesHeader?: LinesHeaderStyles;
    carouselWrapper?: InjectableCss;
}

export const purchasedProductsStyles: PurchasedProductsStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    headerContainer: {
        gap: 0,
        css: css`
            padding: 10px;
            background-color: ${getColor("common.accent")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    itemHeaderGridItem: { width: [0, 0, 6, 6, 6] },
    itemHeaderText: { weight: "bold" },
    priceHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-right: 35px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    priceHeaderText: { weight: "bold" },
    addToCartHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
        `,
    },
    addToCartHeaderText: { weight: "bold" },
};

const styles = purchasedProductsStyles;

const PurchasedProducts = ({
    id,
    fields,
    products,
    canViewPurchasedProducts,
    loadPurchasedProducts,
    isTabletScreen,
}: Props) => {
    React.useEffect(() => {
        if (canViewPurchasedProducts) {
            loadPurchasedProducts({ widgetId: id, purchaseType: fields.purchaseType });
        }
    }, [fields.purchaseType]);

    const productsKey = React.useMemo(
        () => products?.map(p => p.id).join(","), // or JSON.stringify(products)
        [products],
    );
    useSeoSchema(
        {
            key: "productCarousels",
            data: {
                type: fields.purchaseType,
                id,
                name: fields.title,
                items: products || [],
            },
            enabled: !!fields.enableSeo && !!(products && products.length > 0),
        },
        [productsKey, id, fields.purchaseType, fields.title],
    );

    // XNG-Change
    const carouselCardStyles =
        fields.view === CarouselCardType.Condensed
            ? styles.carouselProductCondensedStyles
            : fields.view === CarouselCardType.MiniCard
              ? styles.carouselProductMiniCardStyles
              : isTabletScreen && fields.view === CarouselCardType.Grid
                ? styles.carouselProductMiniCardStyles
                : styles.carouselProductStyles;

    if (!canViewPurchasedProducts || !products || products.length === 0) {
        return null;
    }

    // XNG-Changes
    if (fields.view === CarouselCardType.Table) {
        return (
            <>
                <ContentWrapper alignment={fields.titlePosition} className="wrapper">
                    <Typography {...styles.titleText} variant="h2">
                        {fields.title}
                    </Typography>
                </ContentWrapper>

                {/* XNG-Changes: use LinesHeader to match styles with Cart lines */}
                <LinesHeader
                    leftTitle={translate("Item Description")}
                    rightTitles={[translate("Price/Unit"), translate("QTY"), translate("Add to Cart")]}
                    extendedStyles={styles.linesHeader}
                />

                {products.map(product => (
                    <PurchasedProductCard
                        key={product.id}
                        product={product}
                        widgetId={id}
                        showBrand={fields.showBrand}
                        showPartNumbers={fields.showPartNumbers}
                        showPrice={fields.showPrice}
                        showAvailability={fields.showAvailability}
                        showAddToCart={fields.showAddToCart}
                        showAddToList={fields.showAddToList}
                        showTitle={fields.showTitle}
                    />
                ))}
            </>
        );
    }

    // XNG-Change
    return (
        <StyledWrapper {...styles.carouselWrapper}>
            <ContentWrapper alignment={fields.titlePosition} className="wrapper">
                <Typography {...styles.titleText} variant="h2">
                    {fields.title}
                </Typography>
            </ContentWrapper>
            <XngCarousel spaceBetween={24} scrollbar={true} navigation={!isTabletScreen} slidesPerView="auto">
                {products.map(product => (
                    <ProductCarouselProduct
                        key={product.id}
                        data-test-key={product.id}
                        data-test-selector="carousel_slide"
                        carouselId={id}
                        product={product}
                        showImage={fields.showImage}
                        showBrand={fields.showBrandName}
                        showTitle={fields.showTitle}
                        showPartNumbers={fields.showPartNumbers}
                        showPrice={fields.showPrice}
                        showAddToCart={fields.showAddToCart}
                        showAddToList={fields.showAddToList}
                        showImageBadges={fields.showImageBadges}
                        maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                        showTextBadges={fields.showTextBadges}
                        maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                        showAvailability={fields.showAvailability}
                        view={fields.view}
                        extendedStyles={carouselCardStyles}
                    />
                ))}
            </XngCarousel>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchasedProducts),
    definition: {
        group: "Common",
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.titlePosition,
                displayName: "Title Position",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Left",
                        value: "flex-start",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "flex-end",
                    },
                ],
                defaultValue: "center",
                fieldType: "General",
                sortOrder: 1,
            },
            // XNG-Change
            {
                name: fields.view,
                displayName: "Product Card Type",
                editorTemplate: "DropDownField",
                defaultValue: CarouselCardType.Table,
                fieldType: "General",
                sortOrder: 1,
                options: [
                    {
                        displayName: "Grid",
                        value: CarouselCardType.Grid,
                    },
                    {
                        displayName: "Small Card",
                        value: CarouselCardType.MiniCard,
                    },
                    {
                        displayName: "Condensed",
                        value: CarouselCardType.Condensed,
                    },
                    {
                        displayName: "Table",
                        value: CarouselCardType.Table,
                    },
                ],
            },
            {
                name: fields.purchaseType,
                displayName: "Purchase Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: "recently", displayName: "Recently Purchased" },
                    { value: "frequently", displayName: "Frequently Purchased" },
                ],
                defaultValue: "recently",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            // <XNG-Changes>
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 9,
            },
            // XNG-Change
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "Contextual",
                sortOrder: 10,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 11,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 12,
            },
            {
                name: fields.enableSeo,
                displayName: "include in SEO Data",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
